/** Student Portal (Phase 67) — the small, hand-picked set of fields a
 * student may request a change to. Kept in sync with the backend's own
 * EDITABLE_STUDENT_FIELDS/FIELD_LABELS (studentPortal.validation.ts). */
export const EDITABLE_STUDENT_FIELDS = [
  'phone',
  'email',
  'address',
  'city',
  'postalCode',
  'telephone',
  'whatsappNumber',
  'emergencyContactName',
  'emergencyContactPhone',
  'emergencyContactRelationship',
  // Phase 101 — the expiry date travels as YYYY-MM-DD.
  'passportNumber',
  'passportExpiryDate',
] as const;

export type EditableStudentField = (typeof EDITABLE_STUDENT_FIELDS)[number];

export const FIELD_LABELS: Record<EditableStudentField, string> = {
  phone: 'Phone number',
  email: 'Email address',
  address: 'Address',
  city: 'City',
  postalCode: 'Postal code',
  telephone: 'Telephone',
  whatsappNumber: 'WhatsApp number',
  emergencyContactName: 'Emergency contact name',
  emergencyContactPhone: 'Emergency contact phone',
  emergencyContactRelationship: 'Emergency contact relationship',
  passportNumber: 'Passport number',
  passportExpiryDate: 'Passport expiry date',
};

export interface PortalStudentProfile {
  id: number;
  studentNumber: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
  dateOfBirth: string | null;
  gender: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  telephone: string | null;
  whatsappNumber: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string | null;
  nicNumber: string | null;
  passportNumber: string | null;
  passportExpiryDate: string | null;
  country: { id: number; name: string } | null;
  branch: { id: number; name: string } | null;
}

export interface PortalApplication {
  id: number;
  applicationNumber: string;
  status: string;
  offerLetterDate: string | null;
  coeDate: string | null;
  departureDate: string | null;
  university: { id: number; name: string };
  course: { id: number; name: string };
  visaStatus: { id: number; name: string } | null;
}

/** A fee as the student sees it (2026-11-04): its status and progress only
 * — the portal never receives amounts, currency or totals. */
export interface PortalPaymentChecklistItem {
  categoryId: number;
  name: string;
  description: string | null;
  isRequired: boolean;
  status: 'PAID' | 'PENDING_CONFIRMATION' | 'PARTIALLY_PAID' | 'UNPAID';
  hasPendingPayment: boolean;
  hasRejectedPayment: boolean;
  /** How far along the fee is, 0–100 (100 only when PAID); null when the fee has no set amount. */
  progressPercent: number | null;
}

export interface PortalPaymentChecklist {
  items: PortalPaymentChecklistItem[];
}

export interface PortalDocumentChecklistItem {
  documentTypeId: number;
  documentTypeName: string;
  isMandatory: boolean;
  status: 'MISSING' | 'PENDING' | 'APPROVED' | 'REJECTED';
  satisfied: boolean;
  studentDocumentId: number | null;
  rejectionReason: string | null;
}

export interface PortalDocumentChecklistSummary {
  requiredTotal: number;
  approved: number;
  pendingApproval: number;
  rejected: number;
  missing: number;
  overall: 'APPROVED' | 'PENDING' | 'REJECTED';
}

/** The student's document checklist with the reason when it's empty:
 * NO_PROGRAMME (no application or preferred programme to read requirements
 * from) or NO_REQUIREMENTS (the programme has none configured in Master
 * Data). Mirrors the backend's PortalDocumentRequirements. */
export interface PortalDocumentRequirements {
  status: 'CONFIGURED' | 'NO_PROGRAMME' | 'NO_REQUIREMENTS';
  source: 'APPLICATION' | 'PREFERRED_PROGRAMME' | null;
  university: { id: number; name: string } | null;
  course: { id: number; name: string } | null;
  required: PortalDocumentChecklistItem[];
  optional: PortalDocumentChecklistItem[];
  summary: PortalDocumentChecklistSummary | null;
}

/** The Final Documents List (Phase 99) — the Final Documentation
 * Checklist's items with the student's own latest upload and review. */
export interface PortalFinalDocumentItem {
  key: string;
  letter: string | null;
  text: string;
  conditional: boolean;
  referenceUrl: string | null;
  notApplicable: boolean;
  documentType: { id: number; name: string } | null;
  status: 'NOT_APPLICABLE' | 'MISSING' | 'PENDING' | 'APPROVED' | 'REJECTED';
  document: PortalDocumentSummary | null;
}

/** A document as the Final Documents List shows it. */
export interface PortalDocumentSummary {
  id: number;
  originalName: string;
  mimeType?: string;
  version: number;
  createdAt: string;
  verificationStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  verifiedAt: string | null;
  rejectionReason: string | null;
}

/** "10. Other Documents" (Phase 103): one entry per name the student gave. */
export interface PortalOtherDocumentEntry {
  name: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  document: PortalDocumentSummary;
  /** Earlier versions, newest first. */
  history: PortalDocumentSummary[];
}

export interface PortalFinalDocuments {
  sections: Array<{ number: string; title: string; note: string | null; items: PortalFinalDocumentItem[] }>;
  otherDocuments: {
    number: string;
    title: string;
    /** Null until the Phase 103 migration has created the type. */
    documentType: { id: number; name: string } | null;
    entries: PortalOtherDocumentEntry[];
  };
  progress: {
    total: number;
    uploaded: number;
    approved: number;
    pending: number;
    rejected: number;
    missing: number;
    complete: boolean;
  };
}

export interface PortalNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  readAt: string | null;
  createdAt: string;
}

export interface PortalDashboard {
  student: PortalStudentProfile;
  application: PortalApplication | null;
  paymentChecklist: PortalPaymentChecklist;
  documentChecklist: PortalDocumentChecklistSummary | null;
  documentChecklistItems: PortalDocumentChecklistItem[];
  documentRequirements: PortalDocumentRequirements;
}

export type ChangeRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface PortalChangeRequest {
  id: number;
  requestNumber: string;
  fieldName: EditableStudentField;
  oldValue: string | null;
  newValue: string;
  status: ChangeRequestStatus;
  requestedAt: string;
  decidedAt: string | null;
  decisionReason: string | null;
  decidedBy: { id: number; firstName: string; lastName: string } | null;
}

export interface PortalDocument {
  id: number;
  documentType: { id: number; name: string; requiresExpiry: boolean };
  /** How it is named (Phase 103) — renumbered Final types, or an Other
   * document by the student's own name. */
  name?: string;
  customName?: string | null;
  originalName: string;
  mimeType?: string;
  sizeBytes?: number;
  source?: 'UPLOADED' | 'GENERATED';
  /** YOU = the student uploaded it; IMC = staff uploaded or generated it. */
  uploadedBy?: 'YOU' | 'IMC';
  verificationStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  verifiedAt?: string | null;
  rejectionReason: string | null;
  version: number;
  isLatest: boolean;
  createdAt: string;
}

/** Counselor-side review row (Phase 67) — same shape as PortalChangeRequest
 * plus the student it belongs to, since the review queue spans many
 * students at once. */
export interface ChangeRequestForReview extends PortalChangeRequest {
  student: { id: number; studentNumber: string; firstName: string; lastName: string; branchId: number | null };
}

// Student Portal invitation history (Phase 68)
export type PortalInviteChannel = 'EMAIL' | 'WHATSAPP' | 'MANUAL';
export type PortalInviteStatus = 'PENDING' | 'SENT' | 'FAILED';

export interface PortalInviteRecord {
  id: number;
  channel: PortalInviteChannel;
  status: PortalInviteStatus;
  errorMessage: string | null;
  sentAt: string;
  sentBy: { id: number; firstName: string; lastName: string };
}
